import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type {
  Application,
  Contract,
  Obligation,
  Product,
  StatementOfWork,
  Supplier,
} from './types';
import { NORTH_STAR_PRODUCT_CODE } from './products';
import { addDays, at, groupBy, millions, pad, round } from './support';

/**
 * Suppliers, contracts, statements of work and obligations.
 *
 * Contract value is the largest single lever in the target scenario, so it is
 * modelled properly: a contract carries applications, an SOW carries at most one
 * product, and most contracts carry no product at all. That last point is the
 * realistic one — it is precisely why a large share of third-party spend cannot
 * be attributed to a product without someone doing the work.
 */

interface SupplierSeed {
  code: string;
  name: string;
  category: string;
  tier: Supplier['tier'];
  country: string;
  riskRating: Supplier['riskRating'];
}

const SUPPLIER_SEEDS: readonly SupplierSeed[] = [
  {
    code: 'SUP-MERIDIAN',
    name: 'Meridian Systems',
    category: 'Core Airline Platforms',
    tier: 'Strategic',
    country: 'United States',
    riskRating: 'Medium',
  },
  {
    code: 'SUP-NORTHWIND',
    name: 'Northwind Digital',
    category: 'Operations Software',
    tier: 'Strategic',
    country: 'Ireland',
    riskRating: 'Low',
  },
  {
    code: 'SUP-CALDER',
    name: 'Calder Technologies',
    category: 'Airport & Ground Systems',
    tier: 'Preferred',
    country: 'United Kingdom',
    riskRating: 'Medium',
  },
  {
    code: 'SUP-BRIGHTPATH',
    name: 'Brightpath Consulting',
    category: 'Consulting & Systems Integration',
    tier: 'Strategic',
    country: 'Netherlands',
    riskRating: 'Low',
  },
  {
    code: 'SUP-VANTAGE',
    name: 'Vantage IT Services',
    category: 'Managed Services',
    tier: 'Preferred',
    country: 'India',
    riskRating: 'High',
  },
  {
    code: 'SUP-HALLOWAY',
    name: 'Halloway Data Partners',
    category: 'Data & Analytics',
    tier: 'Preferred',
    country: 'Canada',
    riskRating: 'Low',
  },
  {
    code: 'SUP-KESTREL',
    name: 'Kestrel Cloud Services',
    category: 'Infrastructure & Hosting',
    tier: 'Strategic',
    country: 'United States',
    riskRating: 'Medium',
  },
  {
    code: 'SUP-ORRIN',
    name: 'Orrin Security Group',
    category: 'Security Services',
    tier: 'Tactical',
    country: 'Germany',
    riskRating: 'Medium',
  },
  {
    code: 'SUP-LARKFIELD',
    name: 'Larkfield Staffing',
    category: 'Contingent Workforce',
    tier: 'Tactical',
    country: 'Poland',
    riskRating: 'High',
  },
  {
    code: 'SUP-TERNMOOR',
    name: 'Ternmoor Communications',
    category: 'Messaging & Notifications',
    tier: 'Tactical',
    country: 'Sweden',
    riskRating: 'Low',
  },
];

const OBLIGATION_TEMPLATES: readonly (readonly [Obligation['kind'], string])[] = [
  ['SLA', 'Maintain 99.9% availability measured monthly across production services.'],
  ['SLA', 'Respond to a Severity 1 incident within 15 minutes, 24/7.'],
  ['Reporting', 'Provide a monthly service report within five working days of month end.'],
  [
    'Security',
    'Evidence annual penetration testing and remediate critical findings within 30 days.',
  ],
  ['Security', 'Notify Aurora of any personal-data incident within 24 hours of detection.'],
  [
    'ExitAssistance',
    'Provide up to 12 months of exit assistance at contracted rates on termination.',
  ],
  [
    'Volume',
    'Deliver the committed transaction volume without additional charge up to the annual cap.',
  ],
];

export interface SourcingData {
  suppliers: Supplier[];
  contracts: Contract[];
  statementsOfWork: StatementOfWork[];
  obligations: Obligation[];
}

export function buildSourcing(
  asOf: string,
  rng: Rng,
  applications: readonly Application[],
  products: readonly Product[],
): SourcingData {
  const sourcingRng = rng.fork('sourcing');
  const stamp = provenanceFactory(asOf, rng.fork('sourcing-provenance'));

  const appsByVendor = groupBy(
    applications.filter((a) => a.vendorCode !== null),
    (a) => a.vendorCode ?? '',
  );

  const contracts: Contract[] = [];
  const statementsOfWork: StatementOfWork[] = [];
  const obligations: Obligation[] = [];
  let contractSeq = 0;
  let sowSeq = 0;
  let obligationSeq = 0;

  const suppliers = SUPPLIER_SEEDS.map((seed, index) => {
    const vendorApps = appsByVendor.get(seed.code) ?? [];
    // A supplier's annual spend is the run cost of the applications it supports
    // plus a services uplift — derived, so the supplier page and the application
    // page cannot disagree about the same money.
    const applicationSpend = vendorApps.reduce((sum, app) => sum + app.annualRunCostCents, 0);
    const servicesUplift = Math.round(
      applicationSpend * sourcingRng.float(0.22, 0.55) +
        millions(seed.tier === 'Strategic' ? 18 : seed.tier === 'Preferred' ? 7 : 2.5),
    );
    const annualSpendCents = applicationSpend + servicesUplift;

    const contractCount = seed.tier === 'Strategic' ? 4 : seed.tier === 'Preferred' ? 3 : 2;
    const contractShares = Array.from({ length: contractCount }, () => sourcingRng.float(0.5, 1.5));
    const shareTotal = contractShares.reduce((sum, s) => sum + s, 0);

    for (let i = 0; i < contractCount; i += 1) {
      contractSeq += 1;
      const code = `CTR-${pad(contractSeq, 4)}`;
      const annualValueCents = Math.round((annualSpendCents * at(contractShares, i)) / shareTotal);
      const startDate = addDays(asOf, -sourcingRng.int(180, 1800));
      const termYears = sourcingRng.int(2, 6);
      const endDate = addDays(startDate, termYears * 365);
      const type = at(['MSA', 'Licence', 'ManagedService', 'Subscription'] as const, i % 4);
      const contractApps = sourcingRng.sample(
        vendorApps.map((a) => a.businessCode),
        Math.max(1, Math.ceil(vendorApps.length / contractCount)),
      );

      contracts.push({
        id: idFor('Contract', code),
        businessCode: code,
        name: `${seed.name} — ${type === 'MSA' ? 'Master Services Agreement' : type === 'Licence' ? 'Software Licence' : type === 'ManagedService' ? 'Managed Service Agreement' : 'Subscription Agreement'} ${pad(i + 1, 2)}`,
        supplierCode: seed.code,
        type,
        startDate,
        endDate,
        noticeDays: at([30, 60, 90, 180], i % 4),
        annualValueCents,
        totalValueCents: annualValueCents * termYears,
        autoRenews: sourcingRng.chance(0.45),
        applicationCodes: contractApps,
        provenance: stamp(SOURCE_SYSTEMS.contractManager, `CM-${code}`, {
          meanAgeDays: 26,
          meanConfidence: 0.86,
          dataClassification: 'Confidential',
        }),
      });

      for (const [kind, description] of sourcingRng.sample(OBLIGATION_TEMPLATES, 3)) {
        obligationSeq += 1;
        const obligationCode = `OBL-${pad(obligationSeq, 5)}`;
        obligations.push({
          id: idFor('Obligation', obligationCode),
          businessCode: obligationCode,
          contractCode: code,
          description,
          kind,
          dueDate: kind === 'Reporting' ? addDays(asOf, sourcingRng.int(-20, 40)) : null,
          status: sourcingRng.weighted([
            ['Met', 68],
            ['NotYetDue', 18],
            ['AtRisk', 10],
            ['Breached', 4],
          ] as const),
          provenance: stamp(SOURCE_SYSTEMS.contractManager, `CM-${obligationCode}`, {
            meanAgeDays: 19,
            meanConfidence: 0.8,
          }),
        });
      }
    }

    return {
      id: idFor('Supplier', seed.code),
      businessCode: seed.code,
      name: seed.name,
      category: seed.category,
      tier: seed.tier,
      countryOfRegistration: seed.country,
      riskRating: seed.riskRating,
      annualSpendCents,
      provenance: stamp(SOURCE_SYSTEMS.procurement, `PC-SUP-${pad(index + 1, 4)}`, {
        meanAgeDays: 15,
        meanConfidence: 0.89,
      }),
    };
  });

  // ── Statements of work ─────────────────────────────────────────────────────
  // Only about half of the SOWs name a product. The rest are the honest gap:
  // spend that a finance business partner can see but cannot yet attribute.
  // The north-star product's third-party spend runs through its own named
  // managed-service contract below, so it is excluded from the random pool.
  const activeProducts = products.filter(
    (p) => p.lifecycle === 'Active' && p.businessCode !== NORTH_STAR_PRODUCT_CODE,
  );
  for (const contract of contracts) {
    const sowCount = sourcingRng.int(1, 3);
    for (let i = 0; i < sowCount; i += 1) {
      sowSeq += 1;
      const code = `SOW-${pad(sowSeq, 5)}`;
      const startDate = addDays(contract.startDate, sourcingRng.int(0, 500));
      const namesProduct = sourcingRng.chance(0.52) && activeProducts.length > 0;
      statementsOfWork.push({
        id: idFor('StatementOfWork', code),
        businessCode: code,
        name: `${contract.name.split(' — ')[0] ?? 'Supplier'} SOW ${pad(sowSeq, 3)}`,
        contractCode: contract.businessCode,
        productCode: namesProduct ? sourcingRng.pick(activeProducts).businessCode : null,
        startDate,
        endDate: addDays(startDate, sourcingRng.int(90, 720)),
        valueCents: Math.round(contract.annualValueCents * sourcingRng.float(0.08, 0.42)),
        deliverable: at(
          [
            'Application support and maintenance',
            'Platform modernisation increment',
            'Regulatory change implementation',
            'Integration build and test',
            'Data migration and reconciliation',
            'Service transition and knowledge transfer',
          ],
          sowSeq % 6,
        ),
        status: sourcingRng.weighted([
          ['Active', 62],
          ['Complete', 28],
          ['Draft', 7],
          ['Terminated', 3],
        ] as const),
        provenance: stamp(SOURCE_SYSTEMS.procurement, `PC-${code}`, {
          meanAgeDays: 21,
          meanConfidence: 0.77,
        }),
      });
    }
  }

  // ── Crew Operations managed service ────────────────────────────────────────
  // Named explicitly because the north-star product's cost has to reconcile to
  // a real contract rather than appearing from nowhere on the financials tab.
  contractSeq += 1;
  const crewContractCode = 'CTR-CREWOPS-MS';
  contracts.push({
    id: idFor('Contract', crewContractCode),
    businessCode: crewContractCode,
    name: 'Northwind Digital — Crew Operations Managed Service',
    supplierCode: 'SUP-NORTHWIND',
    type: 'ManagedService',
    startDate: '2023-04-01',
    endDate: '2028-03-31',
    noticeDays: 180,
    annualValueCents: millions(78.4),
    totalValueCents: millions(392),
    autoRenews: false,
    applicationCodes: ['APP-0010', 'APP-0011', 'APP-0013', 'APP-0014'],
    provenance: stamp(SOURCE_SYSTEMS.contractManager, `CM-${crewContractCode}`, {
      meanAgeDays: 8,
      meanConfidence: 0.95,
      dataClassification: 'Confidential',
    }),
  });
  sowSeq += 1;
  statementsOfWork.push({
    id: idFor('StatementOfWork', 'SOW-CDR-01'),
    businessCode: 'SOW-CDR-01',
    name: 'Crew Disruption Recovery — run, enhance and transform',
    contractCode: crewContractCode,
    productCode: 'PRD-CDR',
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    valueCents: millions(64.2),
    deliverable:
      'Recovery engine operation, optimisation increments and rule-engine transformation.',
    status: 'Active',
    provenance: stamp(SOURCE_SYSTEMS.procurement, 'PC-SOW-CDR-01', {
      meanAgeDays: 6,
      meanConfidence: 0.94,
    }),
  });
  for (const [kind, description] of OBLIGATION_TEMPLATES.slice(0, 4)) {
    obligationSeq += 1;
    const obligationCode = `OBL-${pad(obligationSeq, 5)}`;
    obligations.push({
      id: idFor('Obligation', obligationCode),
      businessCode: obligationCode,
      contractCode: crewContractCode,
      description,
      kind,
      dueDate: kind === 'Reporting' ? addDays(asOf, 6) : null,
      status: kind === 'SLA' ? 'Met' : 'NotYetDue',
      provenance: stamp(SOURCE_SYSTEMS.contractManager, `CM-${obligationCode}`, {
        meanAgeDays: 5,
        meanConfidence: 0.93,
      }),
    });
  }

  return { suppliers, contracts, statementsOfWork, obligations };
}

/** Total annual third-party commitment, used by the sourcing KPIs. */
export function totalContractedValue(contracts: readonly Contract[]): number {
  return round(
    contracts.reduce((sum, contract) => sum + contract.annualValueCents, 0),
    0,
  );
}

export const CREW_MANAGED_SERVICE_CONTRACT = 'CTR-CREWOPS-MS';
